import { useEffect, useRef } from 'react';
import { useConfigurationStore } from '@adloid-technologies/discover-sdk/dist/esm/store/configuration-store';
import { usePackageStore } from '@adloid-technologies/discover-sdk/dist/esm/store/package-store';
import { LoaderState } from '@adloid-technologies/discover-sdk/dist/esm/store/loader-store';
import { AddPackage, RemovePackage } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/packages';
import { RemoveMultipleAccessoriesWithLoading } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/accessory';
import { GenerateConflicts } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/conflicts';
import { uimetadata, metadata } from '../utils/metadata';
import { ColorPackageLock } from '../types/ColorPackageLock';

/** The color a variant loads by default for the given color type (interior/exterior),
 * derived from its defaultSkus - not hardcoded per model, so any lock config's
 * "revert to default" works generically. */
function getDefaultColorId(modelId: string, variantID: string | undefined, colorType: 'interior' | 'exterior'): string | undefined {
  if (!variantID) return undefined;
  const variantData = (metadata as any)?.models?.[modelId]?.variants?.[variantID];
  const identifier = colorType === 'interior' ? variantData?.interiorColorIdentifier : variantData?.exteriorColorIdentifier;
  const defaultSku = variantData?.defaultSkus?.find((s: any) => s.id === identifier)?.sku;
  if (!defaultSku) return undefined;
  const colors = (metadata as any)?.models?.[modelId]?.colors?.[colorType] ?? {};
  return Object.keys(colors).find((id) => colors[id]?.sku === defaultSku && colors[id]?.relevantVariants?.includes(variantID));
}

/**
 * Generic, data-driven color<->package lock: for each entry declared in
 * `uimetadata.models[modelId].colorPackageLocks`, selecting the color
 * auto-applies the package and vice versa - and, symmetrically, removing
 * either side breaks the lock and reverts the other side (the package is
 * removed if the color changes away; the color reverts to the variant's
 * default if the package is removed). Not part of the discover-sdk schema
 * (that package has no such mechanism - accessoriesToReload only refreshes
 * an already-active accessory after a color swap, one-directional).
 * Declare locks per-model in that model's ui-metadata.ts; no per-brand
 * branching lives here.
 */
export function useColorPackageLocks(modelId: string): void {
  const { configuration, setConfiguration } = useConfigurationStore();
  const { packageIDs } = usePackageStore();
  const lastKeyRef = useRef<string>('');
  const prevRef = useRef<{ variantID?: string; interiorColorID?: string; exteriorColorID?: string; packageIDs: string[] }>({
    packageIDs: [],
  });

  const variantID = configuration?.variantID;
  const interiorColorID = configuration?.interiorColorID;
  const exteriorColorID = configuration?.exteriorColorID;

  useEffect(() => {
    const locks: ColorPackageLock[] = (uimetadata.models as any)?.[modelId]?.colorPackageLocks ?? [];
    if (!configuration || locks.length === 0) return;

    const key = `${variantID}|${interiorColorID}|${exteriorColorID}|${packageIDs.join(',')}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    // Capture the previous committed state before overwriting it, so the async
    // work below diffs against "what was true a moment ago" - this is what lets
    // it tell a fresh single-sided selection apart from the other side having
    // just been removed.
    const prev = prevRef.current;
    prevRef.current = { variantID, interiorColorID, exteriorColorID, packageIDs: [...packageIDs] };

    const isVariantLoading = LoaderState.getState().isVariantLoading;

    (async () => {
      for (const lock of locks) {
        if (lock.variantId !== variantID) continue;

        const colorKey = lock.colorType === 'interior' ? 'interiorColorID' : 'exteriorColorID';
        const activeColorId = lock.colorType === 'interior' ? interiorColorID : exteriorColorID;
        const prevActiveColorId = lock.colorType === 'interior' ? prev.interiorColorID : prev.exteriorColorID;

        const colorActive = activeColorId === lock.colorId;
        const wasColorActive = prevActiveColorId === lock.colorId;
        const packageActive = packageIDs.includes(lock.packageId);
        const wasPackageActive = prev.packageIDs.includes(lock.packageId);

        if (colorActive && !packageActive) {
          if (wasPackageActive) {
            // The package was just removed while the locked color is still
            // active - break the lock the other way: revert the color to the
            // variant's default rather than re-applying the package.
            const defaultColorId = getDefaultColorId(modelId, variantID, lock.colorType);
            if (defaultColorId && defaultColorId !== lock.colorId) {
              setConfiguration({ ...configuration, [colorKey]: defaultColorId });
            }
          } else {
            await AddPackage(lock.packageId, isVariantLoading);
          }
        } else if (packageActive && !colorActive) {
          if (wasColorActive) {
            // The locked color was just changed away while the package is
            // still active - break the lock the other way: remove the package
            // rather than forcing the color back.
            await RemovePackage(lock.packageId, isVariantLoading);
          } else {
            // Mirrors the SDK's own <Color> click handler (conflict cleanup),
            // but deliberately skips PauseExecutionForConflict - this is an
            // automatic reaction to the user's own package choice, not a new
            // decision that needs re-confirming.
            const trigger =
              lock.colorType === 'interior'
                ? { type: 'interior-color-change' as const, colorId: lock.colorId }
                : { type: 'exterior-color-change' as const, colorId: lock.colorId };
            const conflicts = GenerateConflicts({
              trigger,
              extColorSelected: configuration.exteriorColorID,
              intColorSelected: configuration.interiorColorID,
            } as any);
            if (conflicts.accessoryIDsGettingRemoved.length > 0) {
              await RemoveMultipleAccessoriesWithLoading(conflicts.accessoryIDsGettingRemoved, 'accessory', false);
            }
            if (conflicts.packageIDsGettingRemoved.length > 0) {
              for (const pkgId of conflicts.packageIDsGettingRemoved) await RemovePackage(pkgId, false);
            }
            setConfiguration({ ...configuration, [colorKey]: lock.colorId });
          }
        }
      }
    })();
  }, [modelId, variantID, interiorColorID, exteriorColorID, packageIDs, configuration, setConfiguration]);
}
